/**
 * Frontend utilities for handling UTF-8 file encoding in uploads
 */

/**
 * Safely encodes a filename to UTF-8 for FormData uploads
 * Handles common encoding issues in browser File objects
 */
export function safeEncodeFilename(filename: string): string {
  if (!filename) return filename;
  
  try {
    // Check if filename is already properly encoded
    const decoded = decodeURIComponent(encodeURIComponent(filename));
    if (decoded === filename) {
      return filename;
    }
    
    // If encoding/decoding changes the filename, it might have encoding issues
    // Try to clean it up by normalizing Unicode
    return filename.normalize('NFC');
  } catch (error) {
    console.warn('Failed to encode filename:', error);
    return filename;
  }
}

/**
 * Safely appends a file to FormData with proper UTF-8 filename encoding
 */
export function appendFileToFormData(
  formData: FormData, 
  fieldName: string, 
  file: File, 
  customFilename?: string
): void {
  const filename = customFilename || file.name;
  const safeFilename = safeEncodeFilename(filename);
  
  // Create a new File object with the safe filename if needed
  if (safeFilename !== file.name) {
    const newFile = new File([file], safeFilename, {
      type: file.type,
      lastModified: file.lastModified
    });
    formData.append(fieldName, newFile, safeFilename);
  } else {
    formData.append(fieldName, file, safeFilename);
  }
}

/**
 * Creates FormData with properly encoded file uploads
 */
export function createFormDataWithFiles(
  files: File[], 
  fieldName: string = 'files',
  additionalData?: Record<string, any>
): FormData {
  const formData = new FormData();
  
  // Add additional data first
  if (additionalData) {
    Object.entries(additionalData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, typeof value === 'string' ? value : JSON.stringify(value));
      }
    });
  }
  
  // Add files with safe encoding
  files.forEach((file) => {
    appendFileToFormData(formData, fieldName, file);
  });
  
  return formData;
}

/**
 * Safely displays a filename in UI components
 * Handles potential encoding issues for display purposes
 */
export function displaySafeFilename(filename: string, maxLength: number = 30): string {
  if (!filename) return '';
  
  try {
    const safeFilename = safeEncodeFilename(filename);
    return safeFilename.length > maxLength 
      ? safeFilename.slice(0, maxLength - 3) + '...' 
      : safeFilename;
  } catch (error) {
    console.warn('Failed to process filename for display:', error);
    return filename.length > maxLength 
      ? filename.slice(0, maxLength - 3) + '...' 
      : filename;
  }
} 